import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import type { Blueprint } from 'js/src/client';
import { BlueprintPreviewTile } from 'js/src/components/app/template-preview/BlueprintPreviewTile/BlueprintPreviewTile';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import {
    getCustomBlueprintsList,
    isBlueprintsLoading,
    loadCustomerBlueprintsOperation,
} from 'js/src/data/blueprints';
import { useBlueprintThumbnail } from 'js/src/data/blueprints/blueprint-hooks';
import type { SketchBlueprintsModalContext } from 'js/src/data/modals';
import { getModalContext, hideModal, isSketchBlueprintsModalShowing } from 'js/src/data/modals';
import { useAppDispatch } from 'js/src/libs/RootState';
import { useLuminaryOperations } from 'js/src/luminary/luminary-hooks';

type BlueprintTileWrapperProps = {
    blueprint: Blueprint;
    onClick: (blueprint: Blueprint) => void;
};

const BlueprintTileWrapper: React.FC<BlueprintTileWrapperProps> = ({ blueprint, onClick }) => {
    const canvasPreview = useBlueprintThumbnail(blueprint.id);

    const handleClick = useCallback(() => {
        onClick(blueprint);
    }, [blueprint, onClick]);

    return (
        <BlueprintPreviewTile
            name={blueprint.name}
            canvasPreview={canvasPreview}
            narrativeThread={blueprint.narrative_thread}
            onTileClick={handleClick}
            buttonText="Select"
        />
    );
};

type BlueprintSectionProps = {
    header: string;
    blueprints: Blueprint[];
    onSelect: (blueprint: Blueprint) => void;
    className?: string;
    emptyMessage?: React.ReactNode;
};

const BlueprintSection: React.FC<BlueprintSectionProps> = ({
    header,
    blueprints,
    onSelect,
    className = '',
    emptyMessage,
}) => {
    if (blueprints.length === 0 && !emptyMessage) {
        return null;
    }

    return (
        <div className={classNames('blueprint-section', className)}>
            {blueprints.length > 0 && (
                <div className="row gx-3 mb-l5-8">
                    <div className="col">
                        <div className="font-l5 font-l5--h5 text-uppercase">{header}</div>
                    </div>
                </div>
            )}
            {blueprints.length > 0 ? (
                <div className="row g-3">
                    {blueprints.map((blueprint) => (
                        <div className="col-3" key={blueprint.id}>
                            <BlueprintTileWrapper blueprint={blueprint} onClick={onSelect} />
                        </div>
                    ))}
                </div>
            ) : (
                emptyMessage
            )}
        </div>
    );
};

const CustomBlueprintsCallout: React.FC = () => {
    return (
        <Callout
            id="custom-blueprints-callout"
            persistDismissal={true}
            dismissable={true}
            msgType={CALLOUT_TYPES.INFO}
            className="custom-blueprints-callout"
            title="Create your own Blueprints"
            text="Blueprints let you easily create videos with the same style. Create a video and save it as a blueprint to use here."
            ctaText="Go to Blueprints page"
            onCtaClick={() => {
                window.location.href = '/dashboard/blueprints/';
            }}
        />
    );
};

const SketchBlueprintsModal: React.FC = () => {
    const dispatch = useAppDispatch();

    const isShowing = useSelector(isSketchBlueprintsModalShowing);
    const modalContext = useSelector(getModalContext<SketchBlueprintsModalContext>);
    const onSelect = modalContext?.onSelect;

    const isLoading = useSelector(isBlueprintsLoading);
    const customBlueprints = useSelector(getCustomBlueprintsList);

    const { loadCustomerBlueprints } = useLuminaryOperations({
        loadCustomerBlueprintsOperation,
    });

    const closeModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    const handleSelect = useCallback(
        (blueprint: Blueprint) => {
            if (onSelect) {
                onSelect(blueprint);
            }
            dispatch(hideModal());
        },
        [dispatch, onSelect],
    );

    const onHide = useCallback(() => {
        closeModal();
    }, [closeModal]);

    useEffect(() => {
        if (isShowing) {
            loadCustomerBlueprints();
        }
    }, [isShowing, loadCustomerBlueprints]);

    return (
        <Modal
            showing={isShowing}
            onHidden={onHide}
            size={MODAL_SIZES.XL}
            title="Select a blueprint"
            className="sketch-blueprints-modal"
        >
            <p className="font-l5 color-grey-600 mt-l5-0 mb-l5-24">
                Blueprints train the AI to create videos in a repeatable style, flow, and structure.
            </p>
            {isLoading ? (
                <div className="d-flex justify-content-center py-l5-32">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <BlueprintSection
                    header="Your blueprints"
                    blueprints={customBlueprints}
                    onSelect={handleSelect}
                    emptyMessage={<CustomBlueprintsCallout />}
                />
            )}
        </Modal>
    );
};

export default SketchBlueprintsModal;
